// Libraries
import Vue from 'vue'
import Router from 'vue-router'

import store from '../store'

// Utilities
import { getDefaultState } from '../utility/applicationUtil'
import isEmpty from '../utility/isEmpty'
import Language from '../utility/languageUtil'
import Logger from '../utility/loggerUtil'
import RouterUtil from '../utility/routerUtil'
import Session from '../utility/sessionUtil'
import Wait from '../utility/waitUtil'

import { AppNavigationBrands, BrandEnum, NavigationBrands, SettingsConstants } from '../constants'

// Route Groups
import AuthenticatedRoutes from './authenticated'
import UnauthenticatedRoutes from './unauthenticated'
import ApplicationRoutes from './application'

// common to all pages
const ConfirmAccountReplacement = () => import('../components/common/pages/ConfirmAccountReplacement.vue')
const ContactUs = () => import('../components/common/pages/ContactUs.vue')
const ExperianDisclosure = () => import('../components/loans/pages/common/ExperianDisclosure.vue')
const MaintenanceMode = () => import('../components/common/pages/MaintenanceMode.vue')
const PwaInstallApple = () => import('../components/common/pages/PwaInstallApple.vue')
const PwaInstallChrome = () => import('../components/common/pages/PwaInstallChrome.vue')
const PwaInstallRoute = () => import('../components/common/pages/PwaInstallRoute.vue')
const PwaInstallUnsupported = () => import('../components/common/pages/PwaInstallUnsupported.vue')
const TermsConditionsConsent = () => import('../components/common/pages/TermsConditionsConsent.vue')

// TitleBucks
const TitleBucksOptInDisclosures = () => import('../components/loans/pages/tbs/OptInDisclosures.vue')

// TitleMax
const TermsConditions = () => import('../components/loans/pages/tmx/TermsConditions.vue')
const TitleMaxOptInDisclosures = () => import('../components/loans/pages/tmx/OptInDisclosures.vue')

// InstaLoan
const InstaLoanOptInDisclosures = () => import('../components/loans/pages/inl/OptInDisclosures.vue')
const InstaLoanTermsConditions = () => import('../components/loans/pages/inl/InstaloanTermsConditions.vue')

// Cash1Loans
const Cash1LoansDisclosures = () => import('../components/loans/pages/c1l/OptInDisclosures.vue')

// CaliforniaCheckCash
const CaliforniaCheckCashDisclosures = () => import('../components/loans/pages/ccc/OptInDisclosures.vue')

// CommunityChoice
const CommunityChoiceOptInDisclosures = () => import('../components/loans/pages/ccf/OptInDisclosures.vue')

// Check Into Cash
const CheckIntoCashOptInDisclosures = () => import('../components/loans/pages/cic/OptInDisclosures.vue')

// CheckSmart
const CheckSmartOptInDisclosures = () => import('../components/loans/pages/chs/OptInDisclosures.vue')

// easymoney
const EasyMoneyOptInDisclosures = () => import('../components/loans/pages/esm/OptInDisclosures.vue')

// Parent Routes
const Authenticated = () => import('../components/common/pages/Authenticated.vue')
const GenericParent = () => import('../components/common/pages/GenericParent.vue')
const Unauthenticated = () => import('../components/common/pages/Unauthenticated.vue')

Vue.use(Router)

const appBrand = AppNavigationBrands[process.env.VUE_APP_AppBrand]

const routes = [
  {
    path: '/',
    component: Unauthenticated,
    props: true,
    meta: {
      flow: 'Unauthenticated',
      brand: appBrand
    },
    children: UnauthenticatedRoutes()
  },
  {
    path: '/',
    component: Authenticated,
    props: true,
    meta: {
      flow: 'Authenticated',
      brand: appBrand
    },
    children: AuthenticatedRoutes()
  },
  {
    path: '/',
    component: GenericParent,
    props: true,
    meta: {
      flow: 'Application',
      brand: appBrand
    },
    children: ApplicationRoutes()
  },
  {
    path: '/ContactUs',
    name: 'ContactUs',
    component: ContactUs,
    meta: {
      brand: appBrand
    },
    props: true
  },
  {
    path: `/${NavigationBrands.TitleMax}/TermsConditions`,
    name: `${NavigationBrands.TitleMax}-TermsConditions`,
    component: TermsConditions,
    meta: { brand: NavigationBrands.TitleMax },
    props: true
  },
  {
    path: `/${NavigationBrands.InstaLoan}/TermsConditions`,
    name: `${NavigationBrands.InstaLoan}-TermsConditions`,
    component: InstaLoanTermsConditions,
    meta: { brand: NavigationBrands.InstaLoan },
    props: true
  },
  {
    path: `/${NavigationBrands.TitleBucks}/OptInDisclosures`,
    name: `${NavigationBrands.TitleBucks}-OptInDisclosures`,
    component: TitleBucksOptInDisclosures,
    meta: { brand: NavigationBrands.TitleBucks },
    props: true
  },
  {
    path: `/${NavigationBrands.TitleMax}/OptInDisclosures`,
    name: `${NavigationBrands.TitleMax}-OptInDisclosures`,
    component: TitleMaxOptInDisclosures,
    meta: { brand: NavigationBrands.TitleMax },
    props: true
  },
  {
    path: `/${NavigationBrands.InstaLoan}/OptInDisclosures`,
    name: `${NavigationBrands.InstaLoan}-OptInDisclosures`,
    component: InstaLoanOptInDisclosures,
    meta: { brand: NavigationBrands.InstaLoan },
    props: true
  },
  {
    path: `/${NavigationBrands.Cash1Loans}/OptInDisclosures`,
    name: `${NavigationBrands.Cash1Loans}-OptInDisclosures`,
    component: Cash1LoansDisclosures,
    meta: { brand: NavigationBrands.Cash1Loans },
    props: true
  },
  {
    path: `/${NavigationBrands.CheckIntoCash}/OptInDisclosures`,
    name: `${NavigationBrands.CheckIntoCash}-OptInDisclosures`,
    component: CheckIntoCashOptInDisclosures,
    meta: { brand: NavigationBrands.CheckIntoCash },
    props: true
  },
  {
    path: `/${NavigationBrands.CheckSmart}/OptInDisclosures`,
    name: `${NavigationBrands.CheckSmart}-OptInDisclosures`,
    component: CheckSmartOptInDisclosures,
    meta: { brand: NavigationBrands.CheckSmart },
    props: true
  },
  {
    path: `/${NavigationBrands.CaliforniaCheckCash}/OptInDisclosures`,
    name: `${NavigationBrands.CaliforniaCheckCash}-OptInDisclosures`,
    component: CaliforniaCheckCashDisclosures,
    meta: { brand: NavigationBrands.CaliforniaCheckCash },
    props: true
  },
  {
    path: `/${NavigationBrands.CommunityChoice}/OptInDisclosures`,
    name: `${NavigationBrands.CommunityChoice}-OptInDisclosures`,
    component: CommunityChoiceOptInDisclosures,
    meta: { brand: NavigationBrands.CommunityChoice },
    props: true
  },
  {
    path: `/${NavigationBrands.EasyMoney}/OptInDisclosures`,
    name: `${NavigationBrands.EasyMoney}-OptInDisclosures`,
    component: EasyMoneyOptInDisclosures,
    meta: { brand: NavigationBrands.EasyMoney },
    props: true
  },
  {
    path: '/MaintenanceMode',
    name: 'MaintenanceMode',
    component: MaintenanceMode,
    meta: { brand: appBrand },
    props: true
  },
  {
    path: '/TermsConditionsConsent',
    name: 'TermsConditionsConsent',
    component: TermsConditionsConsent,
    meta: { brand: appBrand },
    props: true
  },
  {
    path: '/ConfirmAccountReplacement',
    name: 'ConfirmAccountReplacement',
    component: ConfirmAccountReplacement,
    meta: { brand: appBrand },
    props: true
  },
  {
    path: '/ExperianDisclosure',
    name: 'ExperianDisclosure',
    component: ExperianDisclosure,
    meta: { brand: appBrand },
    props: true
  },
  {
    path: '/PwaInstallRoute',
    name: 'PwaInstallRoute',
    component: PwaInstallRoute,
    meta: { brand: appBrand, flow: 'PwaInstall' },
    props: true
  },
  {
    path: '/PwaInstallUnsupported',
    name: 'PwaInstallUnsupported',
    component: PwaInstallUnsupported,
    meta: { brand: appBrand, flow: 'PwaInstall' },
    props: true
  },
  {
    path: '/PwaInstallApple',
    name: 'PwaInstallApple',
    component: PwaInstallApple,
    meta: { brand: appBrand, flow: 'PwaInstall' },
    props: true
  },
  {
    path: '/PwaInstallChrome',
    name: 'PwaInstallChrome',
    component: PwaInstallChrome,
    meta: { brand: appBrand, flow: 'PwaInstall' },
    props: true
  },
  {
    path: '*',
    redirect: '/'
  }
]

const nestedRouter = new Router({
  hashbang: false,
  history: true,
  routes
})

/**
 * Router hook to route to a branded route if no other routes matched
 */
nestedRouter.beforeEach((to, from, next) => {
  if (!isEmpty(to.matched)) {
    // If we matched a route, use it
    next()
  } else {
    // If we don't have a matched route, and we haven't already tried a branded route, try a branded route
    const brand = RouterUtil.getCurrentRouteBrand(from)
    const brandedRoute = { name: `${brand}-${to.name}`, params: to.params, query: to.query }
    if (brand && !isEmpty(nestedRouter.resolve(brandedRoute).resolved.matched)) {
      next(brandedRoute)
    } else {
      next({ name: 'Login' })
    }
  }
})

/**
 * Router hook to route to authenticate with device key or signature if possible
 */
nestedRouter.beforeEach(async (to, from, next) => {
  try {
    Wait.waitStart('router.authenticationCheck')
    const { applicationId, expiration, flowLock, guestPayment, profileId, signature, utmSource } = to.query

    // If we have a profile ID and we are unauthenticated going to an authenticated route or going to loan approval in express (which is not using authGuard and unauth/auth route parents)
    if (isEmpty(profileId) || (!RouterUtil.isRouteInFlow('Authenticated', to) && to.name !== 'LoanApproval') || !isEmpty(Session.getAccessToken())) {
      next()
      return
    }

    const lockedFlow = RouterUtil.getCurrentRouteFlow(to)

    if (isEmpty(signature)) {
      // Perform device key auth if we have the params
      store.dispatch('session/authenticate', {
        profileId,
        flow: RouterUtil.getCurrentRouteFlow(to)
      })
      RouterUtil.addLockedFlow(lockedFlow)

      next()
      return
    }

    if (!isEmpty(utmSource)) {
      store.commit('applicationSession/setUtmParameters', { utmSource })
    }

    // Perform signature auth if we have the params
    await store.dispatch('session/authenticate', {
      profileId,
      applicationId,
      signature,
      expiration,
      path: to.path,
      isGuestPayment: !isEmpty(guestPayment)
    })

    if (flowLock === 'true') {
      RouterUtil.addLockedFlow(lockedFlow)
    }

    next()
  } catch (e) {
    Logger.error('Error on beforeEach.deviceKeyAuthenticate')
    Logger.debug(e)

    if (!isEmpty(e.message) && e.message.includes('PasswordResetRequired')) { // covers Cognito-thrown errors too
      store.commit('session/setInternalRedirect', {
        name: to.name,
        params: to.params,
        query: to.query
      })

      next({ name: 'SecureAccount' })
    } else {
      next()
    }
  } finally {
    Wait.waitEnd('router.authenticationCheck')
  }
})

/**
 * Router hook to prevent deviation from locked flow
 */
nestedRouter.beforeEach(async (to, from, next) => {
  const lockedFlows = RouterUtil.getLockedFlows()
  if (!isEmpty(lockedFlows) && RouterUtil.isRouteInFlow('Authenticated', to) && !lockedFlows.some(flow => RouterUtil.isRouteInFlow(flow, to))) {
    // If they are leaving the flow, direct them to login
    await store.dispatch('session/signout')
    // Send through the success/error messages
    const params = {
      successMessage: to.params && to.params.successMessage ? `${to.params.successMessage} ${Language.$t('global.deep_link_login_to_continue')}` : undefined,
      errorMessage: to.params && to.params.errorMessage ? `${to.params.errorMessage} ${Language.$t('global.deep_link_login_to_continue')}` : undefined
    }
    next({ name: 'Login', params })
  } else {
    next()
  }
})

/**
 * Global guard for maintenance mode
 */
nestedRouter.beforeEach(async (to, from, next) => {
  let nextRoute = {}
  try {
    Wait.waitStart('beforeEach.getServiceStatus')
    await store.cache.dispatch('session/getServiceStatus', undefined, {
      timeout: SettingsConstants.shortCacheTimeout
    }).catch((error) => {
      store.cache.delete('session/getServiceStatus', undefined)
      Logger.error('Error getting service status in beforeEach')
      Logger.debug(error)
    })
    if (!store.getters['session/isServiceAvailable']) {
      nextRoute = { name: 'MaintenanceMode' }
    }
  } catch (e) {
    Logger.error('Error on beforeEach.getServiceStatus')
    Logger.debug(e)
  } finally {
    Wait.waitEnd('beforeEach.getServiceStatus')

    if (isEmpty(nextRoute) || to.name === nextRoute.name) {
      next()
    } else {
      next(nextRoute)
    }
  }
})

/**
 * Route Guard to make sure customer can't access areas outside of their brand
 */
nestedRouter.beforeEach(async (to, from, next) => {
  let nextRoute = {}

  try {
    Wait.waitStart('beforeResolve.getBrands')

    if (isEmpty(Session.getAccessToken())) {
      return
    }

    // If we are logged in, grab the brands
    await store.cache.dispatch('brands/getBrands', undefined, {
      timeout: SettingsConstants.shortCacheTimeout
    }).catch(error => {
      store.cache.delete('brands/getBrands', undefined)
      Logger.error('Error getting brands in beforeEach')
      Logger.debug(error)
    })

    if (store.getters['brands/userBrandCount'] > 0 && !store.getters['brands/hasAvailableProviders']) {
      // If they have brands but no available providers, route to Maintenance Mode
      nextRoute = { name: 'MaintenanceMode' }
      return
    }

    // If they have no brands or some providers, check if route is available
    let routeAvailable = true

    // Only check if the route is protected if it is in the Loans flow
    // Some top level routes may be branded (like Contact Us) but not need protection
    if (RouterUtil.isRouteInFlow('Loans', to)) {
      const routeBrand = RouterUtil.getCurrentRouteBrandEnum(to)
      switch (routeBrand) {
        case BrandEnum.Cash1Loans:
          // Allow no brand users to access Cash 1 Loans branded page to link account
          routeAvailable = store.getters['brands/isCash1Available'] || store.getters['brands/userBrandCount'] === 0
          break

        case BrandEnum.CaliforniaCheckCash:
          // Allow no brand users to access California Check Cash branded page to link account
          routeAvailable = store.getters['brands/isCaliforniaCheckCashAvailable'] || store.getters['brands/userBrandCount'] === 0
          break

        case BrandEnum.CheckSmart:
          // Allow no brand users to access CheckSmart branded page to link account
          routeAvailable = store.getters['brands/isCheckSmartAvailable'] || store.getters['brands/userBrandCount'] === 0
          break

        case BrandEnum.CheckIntoCash:
          // Allow no brand users to access Check Into Cash branded page to link account
          routeAvailable = store.getters['brands/isCheckIntoCashAvailable'] || store.getters['brands/userBrandCount'] === 0
          break

        case BrandEnum.CommunityChoice:
          // Allow no brand users to access Community Choice branded page to link account
          routeAvailable = store.getters['brands/isCommunityChoiceAvailable'] || store.getters['brands/userBrandCount'] === 0
          break

        case BrandEnum.EasyMoney:
          // Allow no brand users to access EasyMoney branded page to link account
          routeAvailable = store.getters['brands/isEasyMoneyAvailable'] || store.getters['brands/userBrandCount'] === 0
          break

        case BrandEnum.TitleBucks:
          // Allow no brand users to access TitleBucks branded page to link account
          routeAvailable = store.getters['brands/isTitleBucksAvailable'] || store.getters['brands/userBrandCount'] === 0
          break

        case BrandEnum.InstaLoan:
          // Allow no brand users to access InstaLoan branded page to link account
          routeAvailable = store.getters['brands/isInstaLoanAvailable']
          break

        case BrandEnum.TitleMax:
          // Allow no brand users to access TitleMax branded page to link account
          routeAvailable = store.getters['brands/isTitleMaxAvailable'] || store.getters['brands/userBrandCount'] === 0
          break
      }
    }

    if (!routeAvailable) {
      nextRoute = { name: 'Home' }
    }
  } catch (e) {
    Logger.error('Error on beforeResolve.getBrands')
    Logger.debug(e)
  } finally {
    Wait.waitEnd('beforeResolve.getBrands')

    if (isEmpty(nextRoute) || to.name === nextRoute.name) {
      next()
    } else {
      next(nextRoute)
    }
  }
})

/**
 * Stops the 'Processing...' overlay that could be happening during page routing.
 */
nestedRouter.afterEach(() => {
  Wait.pushEnd()
})

nestedRouter.beforeEach(async (to, from, next) => {
  if (!RouterUtil.isRouteInFlow('Application', from) && RouterUtil.isRouteInFlow('Application', to)) { // Get application product configs when entering application flows
    const defaultState = store.state.applicationCustomer.state || getDefaultState()
    const loanProductType = store.getters['applicationCustomer/loanProduct']
    await store.dispatch('applicationSession/getProductConfiguration', { state: defaultState, productType: loanProductType }).catch(e => {
      Logger.error('Error getting application product config settings when entering app flow')
      Logger.debug(e)
    })
  }

  next()
})

export default nestedRouter
